/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useState } from 'react';
import { useSettingsDirty } from './use-settings-dirty';

/** A move away from the open screen that may need confirming first. */
export interface GuardedNavigation<T> {
  /** Call instead of navigating directly. */
  request: (target: T) => void;
  /** The destination waiting on a confirmation, or null. */
  pending: T | null;
  /** Leave the current screen and drop the draft. */
  confirmLeave: () => void;
  /** Stay put, draft intact. */
  cancelLeave: () => void;
}

/**
 * Wraps settings navigation so an unsaved draft is confirmed rather than
 * dropped. The destination is whatever the page navigates by: a tab, or a
 * tab plus the setting open inside it.
 *
 * @param navigate - The unguarded navigation that actually moves.
 * @returns The guarded request plus the confirmation state.
 */
export function useGuardedNavigate<T>(navigate: (target: T) => void): GuardedNavigation<T> {
  const { isDirty } = useSettingsDirty();
  const [pending, setPending] = useState<T | null>(null);

  const request = useCallback((target: T) => {
    if (isDirty) {
      setPending(() => target);
      return;
    }
    navigate(target);
  }, [isDirty, navigate]);

  // Navigating from inside the state updater would run the side effect twice
  // under StrictMode's double-invoked reducer, so it reads the value instead.
  const confirmLeave = useCallback(() => {
    if (pending !== null) navigate(pending);
    setPending(null);
  }, [pending, navigate]);

  const cancelLeave = useCallback(() => setPending(null), []);

  return { request, pending, confirmLeave, cancelLeave };
}
